import React, { FC, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { MyAccountContainer } from '@components/organisms/MyAccountContainer';
import { Table } from '@components/atoms/Table';
import { Card } from '@components/atoms/Card';
import { Typography, TypographyTagVariants } from '@components/atoms/Typography';
import { Space } from '@components/atoms/Space';
import { Anchor, AnchorMode } from '@components/atoms/Anchor';
import { SITE_URLS } from '@constants';
import { useGetMyAccountAddressesDelete } from '@services/useGetMyAccountAddressesDelete';
import { useNotifications } from '@hooks/useNotifications';
import { useGetMyAccountAddresses } from '@services/useGetMyAccountAddresses';
import { useGetMyAccountAddressesCreate } from '@services/useGetMyAccountAddressesCreate';
import { useGetMyAccountAddressesEdit } from '@services/useGetMyAccountAddressesEdit';

export interface ManageAddressesProps {
    addresses: {
        uuid: string;
        name: string;
        lastName: string;
        fiscalNumber?: string;
        type: string;
        address: string;
        complement: string;
        district: string;
        municipality: string;
        postalCode: string;
        region: string;
        primary: string;
        loading?: boolean;
    }[];
}

export const ManageAddresses: FC<ManageAddressesProps> = ({ addresses }) => {
    const { t } = useTranslation();

    const translations = {
        title: t('manage-addresses-component.title'),
        create: t('manage-addresses-component.create'),
    };

    return (
        <MyAccountContainer title={translations.title}>
            <>
                <Space spaceBreakpoints={{ xs: { space: 30 } }} />
                <Space spaceBreakpoints={{ xs: { space: 30 } }} />
                <Anchor classes="mb-3" href={SITE_URLS.myAddressesCreate} mode={AnchorMode.primary} icon="fa-light fa-plus">
                    {translations.create}
                </Anchor>
                <AddressesManagementTable addresses={addresses} />
            </>
        </MyAccountContainer>
    );
};

interface AddressesManagementTableProps {
    addresses: ManageAddressesProps['addresses'];
}

const AddressesManagementTable: FC<AddressesManagementTableProps> = ({ addresses }) => {
    const { t } = useTranslation();
    const {
        request: requestGetMyAccountAddressesDelete,
        error: errorDelete,
        success: successDelete,
        lastResponseTime,
    } = useGetMyAccountAddressesDelete();
    const {
        lastResponseTime: lastResponseTimeCreate,
        errorMessage: errorMessageCreate,
        validationErrors: validationErrorsCreate,
        success: successMessageCreate,
    } = useGetMyAccountAddressesCreate();
    const {
        lastResponseTime: lastResponseTimeEdit,
        errorMessage: errorMessageEdit,
        validationErrors: validationErrorsEdit,
        success: successMessageEdit,
    } = useGetMyAccountAddressesEdit();
    const { request: requestGetMyAccountAddresses } = useGetMyAccountAddresses();
    const { showDefaultNotification } = useNotifications();

    const translations = {
        idShort: t('addresses-management-table-component.id-short'),
        name: t('addresses-management-table-component.name'),
        lastName: t('addresses-management-table-component.last-name'),
        fiscalNumber: t('addresses-management-table-component.fiscal-number'),
        type: t('addresses-management-table-component.type'),
        address: t('addresses-management-table-component.address-1'),
        complement: t('addresses-management-table-component.address-2'),
        postalCode: t('addresses-management-table-component.postal-code'),
        primary: t('addresses-management-table-component.primary'),
        primaryTrue: t('manage-addresses-component.primary.true'),
        primaryFalse: t('manage-addresses-component.primary.false'),
        delivery: t('manage-addresses-component.type.delivery'),
        billing: t('manage-addresses-component.type.billing'),
        confirm: t('manage-addresses-delete-component.confirm'),
    };

    const handleDelete = (uuid: string) => {
        if (uuid) {
            const confirmed = window.confirm(translations.confirm);
            if (confirmed) {
                requestGetMyAccountAddressesDelete({ uuid });
            }
        }
    };

    useEffect(() => {
        if (lastResponseTime) {
            if (errorDelete) {
                showDefaultNotification({ type: 'error', message: errorDelete });
            } else {
                successDelete && showDefaultNotification({ type: 'success', message: successDelete });
                requestGetMyAccountAddresses();
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [lastResponseTime]);

    useEffect(() => {
        if (lastResponseTimeCreate) {
            if (errorMessageCreate || validationErrorsCreate) {
                showDefaultNotification({ type: 'error', message: errorMessageCreate || validationErrorsCreate });
            } else {
                successMessageCreate && showDefaultNotification({ type: 'success', message: successMessageCreate });
                requestGetMyAccountAddresses();
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [lastResponseTimeCreate]);

    useEffect(() => {
        if (lastResponseTimeEdit) {
            if (errorMessageEdit || validationErrorsEdit) {
                showDefaultNotification({ type: 'error', message: errorMessageEdit || validationErrorsEdit });
            } else {
                successMessageEdit && showDefaultNotification({ type: 'success', message: successMessageEdit });
                requestGetMyAccountAddresses();
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [lastResponseTimeEdit]);

    return (
        <Card classes="mb-0">
            <Table
                borderless={true}
                headerCols={[
                    { classes: 'text-end', fit: true, text: translations.primary },
                    { classes: 'text-left', text: translations.name },
                    { classes: 'text-center', hideOnMobile: true, text: translations.lastName },
                    { classes: 'text-center', hideOnMobile: true, text: translations.fiscalNumber },
                    { classes: 'text-center', text: translations.type },
                    { classes: 'text-center', text: translations.address },
                    { classes: 'text-center', text: translations.postalCode },
                    { classes: 'text-left', text: '' },
                ]}
                contentRows={addresses.map((address) => {
                    return [
                        {
                            classes: 'text-center',
                            content: (
                                <Typography fontSize="14">{address.primary ? <i className="fa-solid fa-star" /> : ''}</Typography>
                            ),
                        },
                        { classes: 'text-left', content: <Typography fontSize="14">{address.name}</Typography> },
                        { classes: 'text-left', content: <Typography fontSize="14">{address.lastName}</Typography> },
                        { classes: 'text-center', content: <Typography fontSize="14">{address.fiscalNumber}</Typography> },
                        {
                            classes: 'text-center',
                            content: (
                                <Typography fontSize="14">
                                    {address.type === 'delivery' ? translations.delivery : translations.billing}
                                </Typography>
                            ),
                        },
                        { classes: 'text-left', content: <Typography fontSize="14">{address.address}</Typography> },
                        { classes: 'text-center', content: <Typography fontSize="14">{address.postalCode}</Typography> },
                        {
                            classes: 'text-end',
                            content: (
                                <Table.Options
                                    options={[
                                        <Anchor
                                            key="option-1"
                                            href={SITE_URLS.myAddressesEdit.replace(':uuid', `${address.uuid}`)}
                                            mode={AnchorMode.none}>
                                            <Typography fontSize="16" tagVariant={TypographyTagVariants.span}>
                                                <i className="fa-light fa-pen-to-square" />
                                            </Typography>
                                        </Anchor>,
                                        <button key="option-2" onClick={() => handleDelete(address.uuid)}>
                                            <Typography fontSize="16" tagVariant={TypographyTagVariants.span}>
                                                <i className="fa-light fa-trash-can" />
                                            </Typography>
                                        </button>,
                                    ]}
                                />
                            ),
                        },
                    ];
                })}
            />
        </Card>
    );
};
